import {
  canPlayType
} from "./chunk-Q4QC4S5T.js";
import {
  loadHlsMediaPlaylist
} from "./chunk-JHOOOW2H.js";
import {
  dimensionsToOrientation
} from "./chunk-CXYZ37JF.js";
import {
  AdapterBaseHtmlMedia
} from "./chunk-TWTJHLMH.js";
import {
  getClientLanguages,
  getMatchLanguages,
  implementsStatic,
  orderByLanguage
} from "./chunk-BPZZ3HZX.js";
import {
  PlaybackSessionError
} from "./chunk-7BHS56SI.js";
import {
  getLogger
} from "./chunk-LFIBM62H.js";
import {
  __decorateClass
} from "./chunk-MVGVKF42.js";

// src/adapter/hlsjs/subtitles.ts
var logger = getLogger("HlsJsBuiltinSubtitles");
var HlsJsBuiltinSubtitles = class {
  hls;
  eventEmitter;
  activeTrack;
  lastTrackSwitchByUser;
  // ms since epoch.
  constructor(hls, eventEmitter) {
    this.hls = hls;
    this.eventEmitter = eventEmitter;
    this.handleTextTrackChangeEvent = this.handleTextTrackChangeEvent.bind(this);
    hls.media?.textTracks.addEventListener("change", this.handleTextTrackChangeEvent);
  }
  supportsAddingTracks() {
    return false;
  }
  addSubtitlesTrack() {
    throw new Error("Not supported.");
  }
  setNoSubtitles(options) {
    const forcedTrack = this.getForcedTrack();
    this.switchSubtitles(forcedTrack?.id, options);
  }
  setDefaultSubtitles() {
    const options = { byUser: false };
    const defaultTrack = this.getDefaultTrack();
    if (defaultTrack) {
      this.switchSubtitles(defaultTrack.id, options);
      return;
    }
    this.setNoSubtitles(options);
  }
  setAutoSubtitles() {
    const options = { byUser: false };
    const defaultTrack = this.getDefaultTrack();
    if (defaultTrack) {
      this.switchSubtitles(defaultTrack.id, options);
      return;
    }
    const autoselectTrack = this.getAutoselectTrack();
    if (autoselectTrack) {
      this.switchSubtitles(autoselectTrack.id, options);
      return;
    }
    this.setNoSubtitles(options);
  }
  setActiveSubtitles(id, options) {
    const ident = Number.parseInt(typeof id === "object" ? id.id : id, 10);
    if (this.getAllTracks().find((track) => track.id === ident && !track.forced)) {
      this.switchSubtitles(ident, options);
    }
  }
  getActiveSubtitles() {
    const id = this.hls.subtitleTrack;
    if (id === -1) {
      return void 0;
    }
    const track = this.getAllTracks().find((t) => t.id === id && !t.forced);
    return track ? trackToSubtitle(track) : void 0;
  }
  getAvailableSubtitles() {
    return this.getAllTracks().filter((sub) => !sub.forced).map(trackToSubtitle);
  }
  enableNativeSubtitlesDisplay() {
    if (!this.hls.subtitleDisplay) {
      this.hls.subtitleDisplay = true;
      this.updateCurrentTrackMode();
      this.emitSubtitlesDisplayChange();
    }
  }
  disableNativeSubtitlesDisplay() {
    if (this.hls.subtitleDisplay) {
      this.hls.subtitleDisplay = false;
      this.updateCurrentTrackMode();
      this.emitSubtitlesDisplayChange();
    }
  }
  isNativeSubtitlesDisplayEnabled() {
    return this.hls.subtitleDisplay;
  }
  destroy() {
    this.switchSubtitles(void 0, { byUser: false });
    this.hls.media?.textTracks.removeEventListener("change", this.handleTextTrackChangeEvent);
    disableHlsJsTextTrackController(this.hls);
  }
  getDefaultTrack() {
    return this.getBestLanguageMatch(this.getAllTracks().filter((track) => track.default && !track.forced));
  }
  getAutoselectTrack() {
    return this.getBestLanguageMatch(
      this.getAllTracks().filter((track) => (track.autoselect || track.default) && !track.forced)
    );
  }
  getForcedTrack() {
    return this.getBestLanguageMatch(this.getAllTracks().filter((track) => track.forced));
  }
  getBestLanguageMatch(tracks) {
    if (tracks.length === 0) {
      return void 0;
    }
    const preferredLanguages = getMatchLanguages(getClientLanguages());
    return orderByLanguage(tracks, (track) => track.lang ?? "unknown", preferredLanguages)[0];
  }
  getAllTracks() {
    return this.hls.subtitleTracks;
  }
  switchSubtitles(targetTrackId, options) {
    this.lastTrackSwitchByUser = options?.byUser ? Date.now() : void 0;
    this.hls.subtitleTrack = targetTrackId ?? -1;
  }
  handleTextTrackChangeEvent() {
    if (!this.hls.media) {
      return;
    }
    let newActiveTrack;
    Array.from(this.hls.media.textTracks).filter((track) => track.kind === "subtitles" || track.kind === "captions").forEach((track) => {
      if (track.mode !== "disabled" && newActiveTrack === void 0) {
        newActiveTrack = track;
      }
    });
    if (this.activeTrack !== void 0 && this.activeTrack !== newActiveTrack) {
      this.activeTrack.oncuechange = null;
      this.emitCueEvent();
      this.activeTrack = void 0;
    }
    if (newActiveTrack !== void 0 && this.activeTrack === void 0) {
      this.activeTrack = newActiveTrack;
      this.activeTrack.oncuechange = this.emitCueEvent.bind(this, this.activeTrack);
      this.emitCueEvent(this.activeTrack);
    }
    const byUser = this.lastTrackSwitchByUser !== void 0 && Date.now() - this.lastTrackSwitchByUser < 100;
    this.emitSubtitlesChange(byUser);
  }
  /**
   * hls.js doesn't change between hidden and showing track modes when toggling
   * subtitleDisplay, so get around the problem by deselecting and selecting
   * the current track, if any.
   */
  updateCurrentTrackMode() {
    const currentTrack = this.hls.subtitleTrack;
    if (currentTrack !== -1) {
      this.hls.subtitleTrack = -1;
      this.hls.subtitleTrack = currentTrack;
    }
  }
  emitSubtitlesChange(byUser) {
    this.eventEmitter.emit("playercore.subtitleschange" /* SubtitlesChange */, {
      byUser,
      active: this.getActiveSubtitles()
      // Forced not returned.
    });
  }
  emitSubtitlesDisplayChange() {
    this.eventEmitter.emit("playercore.subtitlesdisplaychange" /* SubtitlesDisplayChange */, {
      nativeEnabled: this.hls.subtitleDisplay
    });
  }
  emitCueEvent(track) {
    this.eventEmitter.emit("playercore.subtitlescueupdate" /* SubtitlesCueUpdate */, {
      activeCues: Array.from(track?.activeCues ?? [])
    });
  }
};
function trackToSubtitle(track) {
  return { id: `${track.id}`, label: track.name, language: track.lang ?? "unknown" };
}
function disableHlsJsTextTrackController(hls) {
  const controller = hls.subtitleTrackController;
  logger.log("Disabling hls.js text track controller");
  controller.onMediaDetaching();
}

// src/adapter/hlsjs/error.ts
var ReducedHlsJsError = class _ReducedHlsJsError extends Error {
  url;
  type;
  reason;
  fatal;
  response;
  context;
  constructor(errorData) {
    super(errorData.details);
    Object.setPrototypeOf(this, _ReducedHlsJsError.prototype);
    this.name = "ReducedHlsJsError";
    this.url = errorData.url;
    this.type = errorData.type;
    this.reason = errorData.reason ?? errorData.error.message;
    this.fatal = errorData.fatal;
    const { text, code } = errorData.response ?? {};
    const { level, url, type } = errorData.context ?? {};
    this.response = { text, code };
    this.context = { level: level !== null ? level : void 0, url, type };
  }
};
function resolveHlsJsErrorType(data, Details, playbackStarted) {
  let errorType = "UNKNOWN_ERROR" /* UNKNOWN_ERROR */;
  switch (data.details) {
    case Details.FRAG_LOAD_ERROR:
    case Details.FRAG_LOAD_TIMEOUT:
    case Details.KEY_LOAD_TIMEOUT:
    case Details.LEVEL_LOAD_ERROR:
    case Details.LEVEL_LOAD_TIMEOUT:
    case Details.MANIFEST_LOAD_ERROR:
    case Details.MANIFEST_LOAD_TIMEOUT:
    case Details.AUDIO_TRACK_LOAD_ERROR:
    case Details.AUDIO_TRACK_LOAD_TIMEOUT:
      errorType = playbackStarted ? "PLAYBACK_STOPPED_NETWORK_ERROR" /* PLAYBACK_STOPPED_NETWORK_ERROR */ : "NO_PLAYBACK_NETWORK_ERROR" /* NO_PLAYBACK_NETWORK_ERROR */;
      break;
    case Details.FRAG_DECRYPT_ERROR:
    case Details.FRAG_PARSING_ERROR:
    case Details.LEVEL_EMPTY_ERROR:
    case Details.LEVEL_PARSING_ERROR:
    case Details.MANIFEST_PARSING_ERROR:
      errorType = playbackStarted ? "PLAYBACK_STOPPED_DECODE_ERROR" /* PLAYBACK_STOPPED_DECODE_ERROR */ : "NO_PLAYBACK_DECODE_ERROR" /* NO_PLAYBACK_DECODE_ERROR */;
      break;
    case Details.MANIFEST_INCOMPATIBLE_CODECS_ERROR:
      errorType = "UNSUPPORTED_FORMAT_ERROR" /* UNSUPPORTED_FORMAT_ERROR */;
      break;
    case Details.BUFFER_APPEND_ERROR:
    case Details.BUFFER_STALLED_ERROR:
    default:
      break;
  }
  return errorType;
}

// src/adapter/hlsjs/adapter.ts
import {
  DestroyableBase,
  DestroyableSet,
  DestroyableAbortController,
  destroyableEventListener as addEventListener,
  destroyableTimeout as startTimeout,
  whenDestroyed
} from "@nrk/destroyable";

// src/utils/round.ts
function round(n, decimals = 0) {
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
}

// src/adapter/hlsjs/adapter.ts
var adapterInfo = {
  id: "HlsJs",
  name: "Hls.js adapter",
  tech: "hls.js",
  version: "hls.js not yet loaded"
};
var MEDIA_RANGE_PRECISION = 1;
var AHEAD_BUFFER_DISTANCE = 60;
var logger2 = getLogger("HlsJsAdapter");
var HlsJsAdapter = class extends AdapterBaseHtmlMedia {
  hlsJsLoader;
  qualityTracker;
  mediaInfo;
  options;
  continueLoadingMediaOnce;
  incomingAutoSeek;
  startTime;
  startTimeLive;
  constructor(context, loadHlsJs, options) {
    super(context);
    this.hlsJsLoader = loadHlsJs;
    this.mediaInfo = { ...context.media };
    this.mediaInfo.isAudioOnly = false;
    this.options = options;
    this.qualityTracker = context.qualityTracker;
    this.applyInitialPlaybackState(context.initialPlaybackState);
  }
  static canPlayType(mediaType) {
    return canPlayType(mediaType);
  }
  static getAdapterInfo() {
    return adapterInfo;
  }
  getAdapterInfo() {
    return {
      ...adapterInfo,
      config: { ...this.options }
    };
  }
  getMediaInfo() {
    return this.mediaInfo;
  }
  async internalPreload(preloadDestroyables, options) {
    const config = {
      autoStartLoad: false,
      // Load master playlist only.
      capLevelToPlayerSize: this.options?.fixedQualityLevel === void 0 && (this.options?.limitQualityToPlayerSize ?? true),
      debug: this.options?.debugHlsJs,
      enableWorker: !this.options?.disableWebWorker
    };
    if (this.options?.backBufferLength !== void 0) {
      config.backBufferLength = this.options.backBufferLength;
    }
    const hlsClass = await this.loadHlsJs();
    const hls = new hlsClass(config);
    this.addEventListener("destroy", () => hls.destroy());
    this.registerHlsJsEventListeners(hlsClass, hls);
    await this.startLoadingMedia(hlsClass, hls);
    this.registerHlsJsErrorListener(hlsClass, hls);
    this.continueLoadingMediaOnce = (destroyables) => this.continueLoadingMedia(hlsClass, hls, destroyables);
    if (options?.loadData !== false || this.startTimeLive !== void 0) {
      await this.continueLoadingMediaOnce?.(preloadDestroyables);
    } else {
      const abortController = new DestroyableAbortController();
      preloadDestroyables.addDestroyable(abortController);
      const hlsMediaPlaylist = await loadHlsMediaPlaylist(hls.levels[hls.firstLevel].uri, {
        signal: abortController.signal
      });
      const playlistEnd = hlsMediaPlaylist.playlistDuration;
      this.handleMediaRangeUpdate(
        { start: 0, end: playlistEnd, complete: hlsMediaPlaylist.fixedEnd, timestamp: Date.now() / 1e3 },
        playlistEnd - 3 * hlsMediaPlaylist.averageSegmentDuration
        // Wait with program date/time until hls.js loads it.
      );
    }
  }
  async internalPlay(requiredState) {
    await this.continueLoadingMediaOnce?.(this);
    await super.internalPlay(requiredState);
  }
  /**
   * hls.js auto-seeks over buffer holes or nudges stalled buffers by
   * auto-seeking a little. See registerHlsJsErrorListener().
   */
  isAutoSeek() {
    if (this.incomingAutoSeek !== void 0) {
      if (Date.now() - this.incomingAutoSeek < 50) {
        logger2.log("Seeking event associated with buffer management. Not changing state.");
        return true;
      }
      this.incomingAutoSeek = void 0;
    }
    return false;
  }
  /**
   * Apply the initial playback state to the adapter. Start times are just
   * stored for later.
   */
  applyInitialPlaybackState(state) {
    const { playbackRate, mute, volume, startTime, startTimeLive } = state ?? {};
    if (playbackRate !== void 0) {
      this.setPlaybackRate(playbackRate);
    }
    if (mute === true) {
      this.mute();
    } else if (mute === false) {
      this.unmute();
    }
    if (volume !== void 0) {
      this.setVolume(volume);
    }
    this.startTime = startTime;
    this.startTimeLive = startTimeLive;
  }
  /**
   * Load hls.js using the method provided in the adapter configuration.
   * There's no validation of what's actually loaded. The adapter will just
   * fail if it's not hls.js.
   */
  async loadHlsJs() {
    const classOrModule = await this.hlsJsLoader();
    const unknownClass = typeof classOrModule === "function" ? classOrModule : classOrModule.default;
    const hlsClass = unknownClass;
    adapterInfo.version = hlsClass.version;
    return hlsClass;
  }
  async startLoadingMedia(hlsClass, hls) {
    const loadingHooks = new DestroyableSet(this);
    let masterPlaylist;
    loadingHooks.addDestroyables([
      on(hls, hlsClass.Events.ERROR, (event, data) => {
        const error = new PlaybackSessionError(
          resolveHlsJsErrorType(data, hlsClass.ErrorDetails, this.playbackStarted),
          new ReducedHlsJsError(data),
          data.details,
          data.fatal
        );
        if (data.fatal) {
          loadingHooks.destroy();
          hls.destroy();
        }
        this.handleError(error);
      }),
      on(hls, hlsClass.Events.MEDIA_ATTACHED, () => {
        logger2.log("Media attached, loading source.");
        hls.loadSource(this.mediaInfo.url);
      }),
      on(hls, hlsClass.Events.MANIFEST_PARSED, (event, data) => {
        logger2.log("Manifest parsed.");
        masterPlaylist = data;
        loadingHooks.destroy();
      })
    ]);
    hls.attachMedia(this.mediaElement);
    await whenDestroyed(loadingHooks);
    if (masterPlaylist === void 0) {
      return;
    }
    this.completeMediaInfo(masterPlaylist);
    if (this.mediaInfo.qualities !== void 0) {
      this.qualityTracker.setAvailableQualities(this.mediaInfo.qualities);
    }
    if (this.options?.fixedQualityLevel !== void 0) {
      hls.loadLevel = this.options.fixedQualityLevel;
    }
    if (masterPlaylist.subtitleTracks?.length && this.subtitleControls === void 0) {
      logger2.log(
        "Creating controls for media provided subtitles:",
        masterPlaylist.subtitleTracks.map((t) => t.name).join(", ")
      );
      this.subtitleControls = new HlsJsBuiltinSubtitles(hls, this.eventEmitter);
    } else {
      disableHlsJsTextTrackController(hls);
    }
    if (this.subtitleControls !== void 0) {
    }
  }
  /**
   * Load media content, potentially with start time. If start time is applied
   * depends on the type of stream, so metadata preloading needs to happen
   * before start time can be applied.
   */
  async continueLoadingMedia(hlsClass, hls, destroyables) {
    this.continueLoadingMediaOnce = void 0;
    logger2.log(
      `Starting to load data with start time: ${this.startTime ?? -1} (start time live: ${this.startTimeLive})`
    );
    hls.startLoad(this.startTime ?? -1);
    const wait = addEventListener(this.mediaElement, "loadedmetadata", () => {
      logger2.log("Got loadedmetadata.");
      if (this.startTimeLive !== void 0) {
        logger2.log("Seeking to startTimeLive.");
        this.seekToLiveTime(this.startTimeLive);
      }
      wait.destroy();
    });
    destroyables.addDestroyable(wait);
    await whenDestroyed(wait);
  }
  /**
   * Update the media info, quality tracker and subtitle controls with data
   * from the HLS master playlist.
   */
  completeMediaInfo(manifest) {
    const isAudioOnly = !!manifest.audio && !manifest.video;
    this.mediaInfo.isAudioOnly = isAudioOnly;
    this.mediaInfo.qualities = manifest.levels.map(({ bitrate, width, height, attrs }, index) => ({
      level: index,
      bitrate,
      averageBitrate: attrs["AVERAGE-BANDWIDTH"] ? Number.parseInt(attrs["AVERAGE-BANDWIDTH"], 10) : void 0,
      frameRate: attrs["FRAME-RATE"] !== void 0 ? parseFloat(attrs["FRAME-RATE"]) : void 0,
      // hls.js defaults to 0x0 dimensions for audio only streams.
      // player-core defaults to undefined.
      width: isAudioOnly ? void 0 : width,
      height: isAudioOnly ? void 0 : height
    }));
    this.mediaInfo.firstQuality = { ...this.mediaInfo.qualities[manifest.firstLevel] };
    const dimensions = this.mediaInfo.qualities.find(
      (quality) => quality.width !== void 0 && quality.height !== void 0
    );
    if (dimensions !== void 0) {
      this.mediaInfo.videoOrientation = dimensionsToOrientation(dimensions.width, dimensions.height);
    }
  }
  registerHlsJsEventListeners(hlsClass, hls) {
    hls.on(hlsClass.Events.LEVEL_SWITCHED, (event, data) => {
      this.qualityTracker.setCurrentQualityByLevel(data.level);
    });
    hls.on(hlsClass.Events.LEVEL_UPDATED, (event, data) => {
      const timestamp = Date.now() / 1e3;
      const details = data.details;
      const playlistDuration = details.totalduration;
      if (playlistDuration <= 0) {
        return;
      }
      const start = round(details.fragments[0].start, MEDIA_RANGE_PRECISION);
      const mediaRange = {
        start,
        end: start + round(playlistDuration, MEDIA_RANGE_PRECISION),
        complete: !details.live,
        timestamp
      };
      const averageTargetDuration = details.averagetargetduration ?? details.targetduration;
      const livePosition = mediaRange.end - 3 * averageTargetDuration;
      const programDateTimeMs = details.fragments[0].programDateTime;
      const programDateTime = programDateTimeMs ? programDateTimeMs / 1e3 : (
        // Make a guess, but only once.
        this.programDateTimeAtPosZero === void 0 ? this.guessHlsProgramDateTime(playlistDuration, averageTargetDuration) : void 0
      );
      this.handleMediaRangeUpdate(mediaRange, livePosition, programDateTime);
    });
    if (this.options?.flushAheadBufferOnSeek === true) {
      this.addDestroyable(
        addEventListener(this.mediaElement, "seeked", () => {
          const currentTime = this.mediaElement.currentTime;
          const buffered = this.mediaElement.buffered;
          let startOffset;
          let endOffset;
          for (let i = 1; i < buffered.length; ++i) {
            if (buffered.start(i) > currentTime + AHEAD_BUFFER_DISTANCE && buffered.start(i) - buffered.end(i - 1) > hls.config.maxBufferHole) {
              startOffset = buffered.start(i);
              endOffset = buffered.end(buffered.length - 1);
              break;
            }
          }
          if (startOffset !== void 0 && endOffset !== void 0) {
            logger2.log(`Flushing ahead buffer after seek: ${startOffset} to ${endOffset}`);
            hls.trigger(hlsClass.Events.BUFFER_FLUSHING, { startOffset, endOffset, type: null });
          }
        })
      );
    }
    if (this.options?.immediateQualityJumps) {
      const MIN_WIDTH_RATIO = 2;
      let switching = false;
      hls.on(hlsClass.Events.LEVEL_SWITCHING, (event, data) => {
        if (hls.currentLevel === -1) {
          return;
        }
        if (switching) {
          switching = false;
          return;
        }
        const currentVideoWidth = hls.levels[hls.currentLevel].width;
        const nextVideoWidth = data.width;
        logger2.debug(`Switching level from ${hls.currentLevel} to ${data.level}`, {
          from: hls.levels[hls.currentLevel].attrs,
          to: data.attrs
        });
        if (nextVideoWidth > currentVideoWidth && (currentVideoWidth <= 0 || nextVideoWidth / currentVideoWidth >= MIN_WIDTH_RATIO)) {
          logger2.log("Switching level immediately!");
          switching = true;
          hls.currentLevel = data.level;
          hls.currentLevel = -1;
        }
      });
    }
  }
  registerHlsJsErrorListener(hlsClass, hls) {
    hls.on(hlsClass.Events.ERROR, (event, data) => {
      if (data.details === hlsClass.ErrorDetails.BUFFER_NUDGE_ON_STALL) {
        this.incomingAutoSeek = Date.now();
        logger2.log("Incoming buffer nudge.");
        return;
      }
      if (data.details === hlsClass.ErrorDetails.BUFFER_SEEK_OVER_HOLE) {
        this.incomingAutoSeek = Date.now();
        logger2.log("Incoming seek over hole.");
        return;
      }
      if (data.details === hlsClass.ErrorDetails.BUFFER_STALLED_ERROR && !data.fatal) {
        const targetDuration = hls.levels[hls.currentLevel].details?.targetduration ?? 10;
        const minMaxBufferLength = targetDuration * 2;
        if (hls.config.maxMaxBufferLength < minMaxBufferLength) {
          logger2.warn("Buffer stalled with small maxMaxBufferLength. Attempting rescue.");
          const loadTimeout = startTimeout(() => {
            logger2.warn("Buffer stalled with small maxMaxBufferLength. Calling startLoad.");
            hls.startLoad();
          }, 5e3);
          this.addDestroyable(loadTimeout);
          hls.once(hlsClass.Events.BUFFER_APPENDED, () => {
            loadTimeout.destroy();
            const nudgeDistance = hls.config.maxBufferHole;
            logger2.log(`Buffer stalled with small maxMaxBufferLength. Nudging +${nudgeDistance}s.`);
            this.incomingAutoSeek = Date.now();
            this.seekToRelativeTime(nudgeDistance);
          });
          hls.config.maxMaxBufferLength = minMaxBufferLength;
          return;
        }
      }
      const error = new PlaybackSessionError(
        resolveHlsJsErrorType(data, hlsClass.ErrorDetails, this.playbackStarted),
        new ReducedHlsJsError(data),
        data.details,
        data.fatal
      );
      this.handleError(error);
    });
  }
};
HlsJsAdapter = __decorateClass([
  implementsStatic()
], HlsJsAdapter);
var on = (hls, event, listener, context) => {
  hls.on(event, listener, context);
  return new class extends DestroyableBase {
    constructor(off) {
      super();
      this.off = off;
    }
    destroy() {
      this.off();
      super.destroy();
    }
  }(() => hls.off(event, listener, context));
};

export {
  HlsJsAdapter
};
